import { Tenant }            from '@app/modules/admin-tenant/models/tenant.model';
import {
	ArchivingContractTypeEnum,
	ArchivingTenantContractType,
	FiskalCurrentTenantContract
}                            from '@app/modules/contract/models/contract.model';
import { RequestActionEnum } from '@core/interfaces/api.interface';
import { LanguageEnum }      from '@models/language';

// ENUMS #############################################################
// API enums *********************************************************
// Scheduling Status
export enum SchedulingStatusEnum {
	ACTIVE = 'active',
	DISABLED = 'disabled'
}

// DAYS
export enum DaysEnum {
	MONDAY = 'monday',
	TUESDAY = 'tuesday',
	WEDNESDAY = 'wednesday',
	THURSDAY = 'thursday',
	FRIDAY = 'friday',
	SATURDAY = 'saturday',
	SUNDAY = 'sunday'
}


// UI enums *********************************************************
// Scheduling Mode
export enum SchedulingModeEnum {
	CLEAR = 'clear',
	DEACTIVATED = 'deactivated',
	GLOBAL = 'global',
	INDIVIDUAL = 'individual',
	RESET = 'reset'
}

export enum ConfigTypeEnum {
	GLOBAL = 'global',
	INDIVIDUAL = 'individual'
}

export enum ArchivingStoreConfigIdentifierEnum {
	ARCHIVING_ENABLED = 'archivingEnabled',
	ARCHIVING_ERS_CHECK = 'archivingErsCheck',
	ARCHIVING_FEATURE_CASHPOINT_CLOSING = 'archivingFeatureCashPointClosing',
	ARCHIVING_FEATURE_CONTRACT_TYPE = 'archivingFeatureContractType',
	ARCHIVING_FEATURE_OPEN_ARCHIVE = 'archivingFeatureOpenArchive',
	ARCHIVING_FEATURE_PROCEDURAL_DOCUMENTATION = 'archivingFeatureProceduralDocumentation',
	ARCHIVING_FEATURE_TSS_LOG = 'archivingFeatureTssLog',
	ARCHIVING_LOCKED = 'archivingLocked',
	ARCHIVING_SYSTEM_PROPERTIES_ENABLED = 'archivingSystemPropertiesEnabled',
	ARCHIVING_STRICT_REFERENCE_CHECK = 'archivingStrictReferenceCheck'
}

export enum ElsterStoreConfigIdentifierEnum {
	ELSTER_FEATURE_TAXATION_REPORTING_ENABLED = 'elsterFeatureTaxationReportingEnabled',
	ELSTER_TAXATION_REPORTING_DISABLED_EXPIRATION = 'elsterTaxationReportingDisabledExpiration',
}

export enum FiskalStoreConfigIdentifierEnum {
	FISKAL_DISABLED_TSS_LOG_EXPIRATION = 'fiskalDisabledTssLogExpiration',
	FISKAL_FEATURE_FCC_DATACENTER_ENABLED = 'fiskalFeatureFccDatacenterEnabled',
	FISKAL_POSTPONED_REMOVAL_ACKNOWLEDGED_LOGS = 'fiskalPostponedRemovalAcknowledgedLogs'
}

export enum ConfigSourceIdentifierEnum {
	ARCHIVE_CONTRACT = 'archiveContract',
	ELSTER_ELSTER_CONFIG = 'elsterElsterConfig',
	ELSTER_ERIC_CONFIG = 'elsterEricConfig',
	FISKAL_CONTRACT = 'fiskalContract',
	FISKAL_SYSTEM = 'fiskalSystem',
	FISKAL_TSS = 'fiskalTss',
	TENANT = 'tenant'
}


// INTERFACES ########################################################
// API interfaces ****************************************************
// SCHEDULING
export interface ISchedulingConfig extends IPeriodConfig {
	status?: SchedulingStatusEnum;
	type?: ConfigTypeEnum;
}

// NOTIFICATION
export interface IEmailNotification {
	emails?: string[];
	language?: LanguageEnum;
}


// UI interfaces *****************************************************
// PERIOD CONFIG
export interface IPeriodConfig {
	days: Array<DaysEnum>;
	startHour: number;
}

// PERIOD
export interface IPeriod {
	hour: number;
	label: number;
	selected: boolean;
}

// DAY ITEM
export interface IDayItem {
	day: DaysEnum;
	selected: boolean;
}

export interface IConfigWarningViewOptions {
	i18n: {
		confirmButton?: string;
		message: string;
		title?: string;
	};
}

export interface IConfigRequestBase {
	configIdentifier: ConfigIdentifierType;
	configType: ConfigTypeEnum;
	getDataFromStoreAtFirst?: boolean;
	requestAction?: RequestActionEnum;
	requestData?: ConfigSourceTypes;
	useAdminApi?: boolean;
}

export interface IArchiveConfigRequestIndividual extends IConfigRequestBase {
	configIdentifier: ArchivingStoreConfigIdentifierEnum;
	configType: ConfigTypeEnum.INDIVIDUAL;
	entityId: string;
}

export interface IArchiveConfigRequestGlobal extends IConfigRequestBase {
	configIdentifier: ArchivingStoreConfigIdentifierEnum;
	configType: ConfigTypeEnum.GLOBAL;
}

export interface IElsterConfigRequestIndividual extends IConfigRequestBase {
	configIdentifier: ElsterStoreConfigIdentifierEnum;
	configType: ConfigTypeEnum.INDIVIDUAL;
	entityId: string;
}

export interface IElsterConfigRequestGlobal extends IConfigRequestBase {
	configIdentifier: ElsterStoreConfigIdentifierEnum;
	configType: ConfigTypeEnum.GLOBAL;
}

export interface IFiskalConfigRequestIndividual extends IConfigRequestBase {
	configIdentifier: FiskalStoreConfigIdentifierEnum;
	configType: ConfigTypeEnum.INDIVIDUAL;
	entityId: string;
}

export interface IFiskalConfigRequestGlobal extends IConfigRequestBase {
	configIdentifier: FiskalStoreConfigIdentifierEnum;
	configType: ConfigTypeEnum.GLOBAL;
}


// TYPES #############################################################
export type ConfigurationsProp = ( keyof Configurations );
export type ConfigurationsProps = ConfigurationsProp[];
export type ConfigIdentifierType =
	ArchivingStoreConfigIdentifierEnum
	| ElsterStoreConfigIdentifierEnum
	| FiskalStoreConfigIdentifierEnum;

/**
 * generates compiler checked source fields for Configuration class
 */
type ConfigurationsSourceConfigTypes = {
	[Property in keyof ( { [key in ConfigSourceIdentifierEnum]: ConfigTypeEnum; } ) as `source${ Capitalize<Property> }Type`]: ConfigTypeEnum;
};

export type ConfigSourceTypes =
	ArchivingTenantContractType
	| ElsterConfig
	| EricConfig
	| FiskalCurrentTenantContract
	| SystemConfig
	| Tenant
	| TssConfig;

export type ConfigRequestTypes =
	ArchiveConfigRequestTypes
	| ElsterConfigRequestTypes
	| FiskalConfigRequestTypes;

// ARCHIVING
export type ArchiveConfigRequestTypes =
	IArchiveConfigRequestIndividual
	| IArchiveConfigRequestGlobal;

export type ArchivingStatusType =
	ArchivingStatus
	| ArchivingStatusItem;

// ELSTER
export type ElsterConfigRequestTypes =
	IElsterConfigRequestIndividual
	| IElsterConfigRequestGlobal;

// FISKAL
export type FiskalConfigRequestTypes =
	IFiskalConfigRequestIndividual
	| IFiskalConfigRequestGlobal;


// ENTITIES ##########################################################
// MODEL USED FOR TRANSITION APP<->API (TransitionalModelObject)
// NOTIFICATION
export class NotificationConfigTMO {
	public tss: EmailNotificationTMO = new EmailNotificationTMO();
}

export class EmailNotificationTMO implements IEmailNotification {
	public emails?: string[] = undefined;
	public language?: LanguageEnum = undefined;
}

// SCHEDULING
export class SchedulingConfigTMO {
	days: Array<DaysEnum> = undefined;
	startHour: number = undefined;
	status?: SchedulingStatusEnum = undefined;
	type?: ConfigTypeEnum = undefined;
}


// ARCHIVING CONFIG
export class ArchivingStatusTMO {
	public enabled: boolean = undefined;
}

export class ArchivingStatusItemTMO extends ArchivingStatusTMO {
	public businessPremisesId: string = undefined;
}

export class ArchivingSystemConfigTMO {
	public enabled: boolean = undefined;
	public ersCheck: boolean = undefined;
	public locked?: boolean = undefined;
	public strictReferenceCheck: boolean = undefined;
}

export class ArchivingSystemPropertiesTMO {
	public enabled: boolean = undefined;
}

// ELSTER CONFIGS
export class ElsterConfigTMO {
	public ericEnabled?: boolean = undefined;
	public type?: ConfigTypeEnum = undefined;
}

export class EricConfigTMO {
	public disabledExpiration?: boolean = undefined;
}

// FISKAL CONFIGS
export class TssConfigTMO {
	public disabledTssLogExpiration?: boolean = undefined;
	public postponedRemovalAcknowledgedLogs?: number = undefined;
}

// System CONFIGS
export class SystemConfigTMO {
	public archiving?: ArchivingSystemConfig = new ArchivingSystemConfig();
	public archivingSystemProperties?: ArchivingSystemProperties = new ArchivingSystemProperties();
	public type?: ConfigTypeEnum = undefined;
}

export class SystemConfigBodyTMO {
	public archiving?: ArchivingSystemConfig = undefined;
	public type?: ConfigTypeEnum = undefined;
}


// MODEL USED IN APPLICATION
export class NotificationConfig extends NotificationConfigTMO {
	public etag?: string = undefined; // Additional property for UI and versioning
	public tss: EmailNotification = new EmailNotification();
	public type?: ConfigTypeEnum = undefined;
}

export class EmailNotification extends EmailNotificationTMO {}

export class SchedulingConfig extends SchedulingConfigTMO {
	public etag?: string = undefined; // Additional property for UI and versioning
}

// ARCHIVING CONFIG
export class ArchivingStatus extends ArchivingStatusTMO {}

export class ArchivingStatusItem extends ArchivingStatusItemTMO {}

export class ArchivingConfigs {
	public systemConfigEtag: string = undefined;
	public systemConfigs: ArchivingSystemConfig = new ArchivingSystemConfig();
	public systemProperties: ArchivingSystemProperties = new ArchivingSystemProperties();
}

export class ArchivingSystemConfig extends ArchivingSystemConfigTMO {}

export class ArchivingSystemProperties extends ArchivingSystemPropertiesTMO {}

// ELSTER CONFIGS
/**
 * In our technical business solution, the Elster configurations are currently spread across two API-Microservices
 * The ElsterConfig in our application reflects the model from the Management-API-Microservice
 */
export class ElsterConfig extends ElsterConfigTMO {
	public taxationReportingEnabled?: boolean = undefined;
	public etag?: string = undefined;
}

/**
 * In our technical business solution, the Elster configurations are currently spread across two API-Microservices
 * The EricConfig in our application reflects the model from the Eric-Wrapper-API-Microservice
 */
export class EricConfig extends EricConfigTMO {
	public etag?: string = undefined;
}

// FISKAL CONFIGS
export class SystemConfig extends SystemConfigTMO {
	public etag?: string = undefined; // Additional property for UI and versioning
	public allowUpdateEnable?: boolean = undefined;
	public archivingConfigs?: ArchivingConfigs = new ArchivingConfigs();
}

export class TssConfig extends TssConfigTMO {}


export class Configurations implements Record<ConfigIdentifierType, any>, ConfigurationsSourceConfigTypes {
	public archivingEnabled: boolean = undefined;
	public archivingErsCheck: boolean = undefined;
	public archivingFeatureCashPointClosing: boolean = undefined;
	public archivingFeatureContractType: ArchivingContractTypeEnum = undefined;
	public archivingFeatureOpenArchive: boolean = undefined;
	public archivingFeatureProceduralDocumentation: boolean = undefined;
	public archivingFeatureTssLog: boolean = undefined;
	public archivingLocked: boolean = undefined;
	public archivingStrictReferenceCheck: boolean = undefined;
	public archivingSystemPropertiesEnabled: boolean = undefined;
	public elsterFeatureTaxationReportingEnabled: boolean = undefined;
	public elsterTaxationReportingDisabledExpiration: boolean = undefined;
	public fiskalDisabledTssLogExpiration: boolean = undefined;
	public fiskalFeatureFccDatacenterEnabled: boolean = undefined;
	public fiskalPostponedRemovalAcknowledgedLogs: number = undefined;
	public sourceArchiveContractType: ConfigTypeEnum = undefined;
	public sourceElsterElsterConfigType: ConfigTypeEnum = undefined;
	public sourceElsterEricConfigType: ConfigTypeEnum = undefined;
	public sourceFiskalContractType: ConfigTypeEnum = undefined;
	public sourceFiskalSystemType: ConfigTypeEnum = undefined;
	public sourceFiskalTssType: ConfigTypeEnum = undefined;
	public sourceTenantType: ConfigTypeEnum = undefined;
}

export const SOURCE_CONFIG_PROPS_MAP: Map<ConfigSourceIdentifierEnum, ConfigIdentifierType[]> =
	new Map<ConfigSourceIdentifierEnum, ConfigIdentifierType[]>( [
		[
			ConfigSourceIdentifierEnum.ARCHIVE_CONTRACT,
			[
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_FEATURE_CASHPOINT_CLOSING,
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_FEATURE_CONTRACT_TYPE,
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_FEATURE_OPEN_ARCHIVE,
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_FEATURE_PROCEDURAL_DOCUMENTATION,
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_FEATURE_TSS_LOG
			]
		],
		[
			ConfigSourceIdentifierEnum.ELSTER_ELSTER_CONFIG,
			[
				ElsterStoreConfigIdentifierEnum.ELSTER_FEATURE_TAXATION_REPORTING_ENABLED
			]
		],
		[
			ConfigSourceIdentifierEnum.ELSTER_ERIC_CONFIG,
			[
				ElsterStoreConfigIdentifierEnum.ELSTER_TAXATION_REPORTING_DISABLED_EXPIRATION
			]
		],
		[
			ConfigSourceIdentifierEnum.FISKAL_CONTRACT,
			[
				FiskalStoreConfigIdentifierEnum.FISKAL_FEATURE_FCC_DATACENTER_ENABLED
			]
		],
		[
			ConfigSourceIdentifierEnum.FISKAL_SYSTEM,
			[
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_ENABLED,
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_ERS_CHECK,
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_LOCKED,
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_STRICT_REFERENCE_CHECK,
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_SYSTEM_PROPERTIES_ENABLED
			]
		],
		[
			ConfigSourceIdentifierEnum.FISKAL_TSS,
			[
				FiskalStoreConfigIdentifierEnum.FISKAL_DISABLED_TSS_LOG_EXPIRATION,
				FiskalStoreConfigIdentifierEnum.FISKAL_POSTPONED_REMOVAL_ACKNOWLEDGED_LOGS
			]
		],
		[
			ConfigSourceIdentifierEnum.TENANT,
			[
				ArchivingStoreConfigIdentifierEnum.ARCHIVING_SYSTEM_PROPERTIES_ENABLED
			]
		]
	] );
